import { addDays, differenceInCalendarDays, format, parseISO, startOfWeek } from "date-fns"
import { ptBR } from "date-fns/locale"
import { stageEstimateKey } from "@/lib/stage-estimates"
import type { MyTask, PipelineStageEstimate, ProductionRun } from "@/types/domain"

/**
 * Cronograma das produções: a partir da data final prevista, as etapas são
 * distribuídas de trás para frente em dias úteis (segunda a sexta), na ordem
 * do fluxo, cada uma com a estimativa de dias do selo. Etapas sem estimativa
 * ficam sem datas e não ocupam tempo. Feriados não são considerados.
 *
 * A mesma regra é aplicada no banco (`private.schedule_production_run`).
 */

export interface StageScheduleInput {
  id: string
  days?: number | null
}

export interface StageSchedule {
  stageId: string
  estimatedDays: number | null
  plannedStartDate: string | null
  plannedEndDate: string | null
}

const isoDatePattern = /^\d{4}-\d{2}-\d{2}$/

export function isIsoDate(value?: string | null): value is string {
  return Boolean(value && isoDatePattern.test(value) && !Number.isNaN(parseISO(value).getTime()))
}

export function toIsoDate(date: Date) {
  return format(date, "yyyy-MM-dd")
}

export function todayIsoDate() {
  return toIsoDate(new Date())
}

export function isWorkingDay(value: string | Date) {
  const day = (typeof value === "string" ? parseISO(value) : value).getDay()
  return day !== 0 && day !== 6
}

/** O dia útil `count` dias antes da data; com `count` 0, a própria data ou o dia útil anterior, se cair no fim de semana. */
export function subtractWorkingDays(isoDate: string, count: number) {
  let date = parseISO(isoDate)
  while (!isWorkingDay(date)) date = addDays(date, -1)
  for (let remaining = count; remaining > 0;) {
    date = addDays(date, -1)
    if (isWorkingDay(date)) remaining -= 1
  }
  return toIsoDate(date)
}

/** Desloca `count` dias úteis (positivo para depois, negativo para antes); a data é antes levada ao dia útil anterior, se cair no fim de semana. */
export function shiftWorkingDays(isoDate: string, count: number) {
  return count < 0 ? subtractWorkingDays(isoDate, -count) : addWorkingDays(subtractWorkingDays(isoDate, 0), count)
}

/** Dias úteis de `start` até `end`, contando os dois. */
export function workingDaysBetween(start: string, end: string) {
  let count = 0
  for (let date = parseISO(start); toIsoDate(date) <= end; date = addDays(date, 1)) if (isWorkingDay(date)) count += 1
  return count
}

/** O dia útil `count` dias depois da data; com `count` 0, a própria data ou o próximo dia útil. */
export function addWorkingDays(isoDate: string, count: number) {
  let date = parseISO(isoDate)
  while (!isWorkingDay(date)) date = addDays(date, 1)
  for (let remaining = count; remaining > 0;) {
    date = addDays(date, 1)
    if (isWorkingDay(date)) remaining -= 1
  }
  return toIsoDate(date)
}

/**
 * Datas planejadas de cada etapa, com as etapas na ordem do fluxo. O tempo é
 * contado em centésimos de dia para que etapas fracionadas (por exemplo, duas
 * de meio dia) dividam o mesmo dia sem erro de arredondamento.
 */
export function scheduleStages(stages: StageScheduleInput[], targetEndDate: string): StageSchedule[] {
  const schedule = new Map<string, StageSchedule>()
  let after = 0
  for (const stage of [...stages].reverse()) {
    const hundredths = stage.days && stage.days > 0 ? Math.round(stage.days * 100) : 0
    if (!hundredths) {
      schedule.set(stage.id, { stageId: stage.id, estimatedDays: null, plannedStartDate: null, plannedEndDate: null })
      continue
    }
    const before = after + hundredths
    schedule.set(stage.id, {
      stageId: stage.id,
      estimatedDays: hundredths / 100,
      plannedStartDate: subtractWorkingDays(targetEndDate, Math.ceil(before / 100) - 1),
      plannedEndDate: subtractWorkingDays(targetEndDate, Math.floor(after / 100)),
    })
    after = before
  }
  return stages.map((stage) => schedule.get(stage.id)!)
}

/** Dias estimados de cada etapa para o selo do produto, ligados pelo nome da etapa. */
export function stageEstimateDays(estimates: PipelineStageEstimate[], imprintId: string, stages: Array<{ id: string; name: string }>) {
  const byName = new Map(estimates.filter((estimate) => estimate.imprintId === imprintId).map((estimate) => [stageEstimateKey(estimate.stageName), estimate.days]))
  return stages.map((stage) => ({ id: stage.id, days: byName.get(stageEstimateKey(stage.name)) ?? null }))
}

/** Uma etapa está atrasada quando o prazo passou e ela ainda não foi concluída. */
export function isOverdue(plannedEndDate: string | null | undefined, done: boolean, today = todayIsoDate()) {
  return !done && Boolean(plannedEndDate) && plannedEndDate! < today
}

export function daysLate(plannedEndDate: string, today = todayIsoDate()) {
  return Math.max(0, differenceInCalendarDays(parseISO(today), parseISO(plannedEndDate)))
}

export type DeadlineProgress =
  | { state: "UNSCHEDULED" }
  | { state: "DONE" }
  | { state: "NOT_STARTED"; totalDays: number }
  | { state: "IN_PROGRESS"; day: number; totalDays: number; percentage: number }
  | { state: "OVERDUE"; daysLate: number }

/** Quanto do período planejado de uma etapa já passou, em dias úteis, contando o dia de hoje. */
export function deadlineProgress(plannedStartDate: string | null | undefined, plannedEndDate: string | null | undefined, done: boolean, today = todayIsoDate()): DeadlineProgress {
  if (done) return { state: "DONE" }
  if (!plannedStartDate || !plannedEndDate) return { state: "UNSCHEDULED" }
  if (isOverdue(plannedEndDate, done, today)) return { state: "OVERDUE", daysLate: daysLate(plannedEndDate, today) }
  const totalDays = Math.max(1, workingDaysBetween(plannedStartDate, plannedEndDate))
  if (today < plannedStartDate) return { state: "NOT_STARTED", totalDays }
  const day = Math.min(totalDays, Math.max(1, workingDaysBetween(plannedStartDate, today)))
  return { state: "IN_PROGRESS", day, totalDays, percentage: Math.round((day / totalDays) * 100) }
}

/** Os cinco dias úteis da semana que contém a data. */
export function workWeek(isoDate: string) {
  const monday = startOfWeek(parseISO(isoDate), { weekStartsOn: 1 })
  return Array.from({ length: 5 }, (_, index) => toIsoDate(addDays(monday, index)))
}

export function shiftWeek(isoDate: string, weeks: number) {
  return toIsoDate(addDays(parseISO(isoDate), weeks * 7))
}

/** “13/10/2026”. */
export function formatDate(isoDate: string) {
  return format(parseISO(isoDate), "dd/MM/yyyy")
}

/** “13/10”. */
export function formatShortDate(isoDate: string) {
  return format(parseISO(isoDate), "dd/MM")
}

/** “seg”, “ter”… */
export function formatWeekday(isoDate: string) {
  return format(parseISO(isoDate), "EEEEEE", { locale: ptBR }).toLocaleLowerCase("pt-BR")
}

/** “13/10 – 17/10”, ou só “13/10” quando começa e termina no mesmo dia. */
export function formatDateRange(start: string, end: string) {
  return start === end ? formatShortDate(start) : `${formatShortDate(start)} – ${formatShortDate(end)}`
}

/** Situação de uma etapa na produção: concluída quando todos os itens a concluíram ou pularam; atual quando algum item está nela. */
export function runStageProgress(run: Pick<ProductionRun, "items">, stageId: string) {
  const states = run.items.filter((item) => item.status !== "CANCELLED").flatMap((item) => item.stageStates.filter((state) => state.stageId === stageId))
  return {
    done: states.length > 0 && states.every((state) => state.status === "DONE" || state.status === "SKIPPED"),
    current: states.some((state) => state.status === "IN_PROGRESS" || state.status === "BLOCKED"),
  }
}

export interface WeekTaskRow {
  task: MyTask
  overdue: boolean
  /** Colunas (0 a 4) ocupadas pela barra na semana. */
  first: number
  last: number
}

/**
 * Tarefas com datas que caem na semana (segunda a sexta). Na semana atual,
 * tarefas atrasadas de semanas anteriores também entram, com a barra até hoje.
 */
export function weekTaskRows(tasks: MyTask[], days: string[], today: string): WeekTaskRow[] {
  const monday = days[0]; const friday = days[days.length - 1]
  const currentWeek = workWeek(today)[0] === monday
  const firstOnOrAfter = (date: string) => { const index = days.findIndex((day) => day >= date); return index === -1 ? days.length - 1 : index }
  const lastOnOrBefore = (date: string) => days.filter((day) => day <= date).length - 1
  return tasks.flatMap((task) => {
    const start = task.plannedStartDate; const end = task.plannedEndDate
    if (!start || !end) return []
    const overdue = isOverdue(end, task.status === "DONE", today)
    if (start <= friday && end >= monday) return [{ task, overdue, first: start < monday ? 0 : firstOnOrAfter(start), last: end > friday ? days.length - 1 : lastOnOrBefore(end) }]
    if (currentWeek && overdue && end < monday) return [{ task, overdue, first: 0, last: lastOnOrBefore(today) }]
    return []
  }).sort((left, right) => left.first - right.first || left.last - right.last || (left.task.plannedEndDate ?? "").localeCompare(right.task.plannedEndDate ?? ""))
}

/** Distribui as barras em linhas, como num calendário: cada barra vai para a primeira linha em que não encosta em outra. */
export function packWeekRows(rows: WeekTaskRow[]): WeekTaskRow[][] {
  const lanes: WeekTaskRow[][] = []
  for (const row of rows) {
    const lane = lanes.find((candidate) => candidate[candidate.length - 1].last < row.first)
    if (lane) lane.push(row)
    else lanes.push([row])
  }
  return lanes
}
